import React from "react";
import cn from "classnames";
import { Link } from "react-router-dom";
import DataTable, { DataColumn } from "../DataTable";
import {
  IdfaImportResult,
  IdfaImportStatus,
  IdfaImportedFile,
} from "../../api/idfaReports";
import { importSummary } from "./useIdfaUpload";
import styles from "./styles.module.scss";

const STATUS_LABEL: Record<IdfaImportStatus, string> = {
  imported: "Imported",
  duplicate: "Already imported",
  unmatched: "No order",
  ambiguous: "Ambiguous",
  skipped: "Skipped",
  failed: "Failed",
};

// Files that did not land first: they are what the admin has to act on.
const STATUS_ORDER: IdfaImportStatus[] = [
  "failed",
  "ambiguous",
  "unmatched",
  "skipped",
  "duplicate",
  "imported",
];

export const ImportStatus: React.FC<{ status: IdfaImportStatus }> = ({
  status,
}) => (
  <span className={cn(styles.pill, styles[`pill-${status}`])}>
    {STATUS_LABEL[status]}
  </span>
);

const noteOf = (file: IdfaImportedFile) =>
  [
    file.reason,
    file.invalidIdfas
      ? `${file.invalidIdfas} IDFA${file.invalidIdfas > 1 ? "s" : ""} cut short by the contractor`
      : null,
    file.skippedRows ? `${file.skippedRows} rows without IDFA or time` : null,
  ]
    .filter(Boolean)
    .join("; ");

const columns: DataColumn<IdfaImportedFile>[] = [
  {
    key: "status",
    title: "Status",
    width: 120,
    fixed: true,
    render: (file) => <ImportStatus status={file.status} />,
    sorter: (a, b) =>
      STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status),
  },
  {
    key: "order",
    title: "Order",
    width: 104,
    render: (file) =>
      file.order ? (
        <Link
          to={`/campaigns/${file.order.orderType}/${file.order.orderId}`}
          className={styles.link}
        >
          #{file.order.hashId ?? file.order.orderId}
        </Link>
      ) : (
        "—"
      ),
    sorter: (a, b) => (a.order?.hashId || 0) - (b.order?.hashId || 0),
  },
  {
    key: "file",
    title: "File",
    width: 300,
    mobile: "full",
    render: (file) => <span title={file.sourcePath}>{file.fileName}</span>,
    sorter: (a, b) => a.sourcePath.localeCompare(b.sourcePath),
  },
  {
    key: "country",
    title: "Country",
    width: 150,
    render: (file) => file.country || "—",
    sorter: (a, b) => (a.country || "").localeCompare(b.country || ""),
  },
  {
    key: "rows",
    title: "Rows",
    width: 96,
    render: (file) => file.rows.toLocaleString("en-US"),
    sorter: (a, b) => a.rows - b.rows,
  },
  {
    key: "note",
    title: "Note",
    width: 420,
    mobile: "full",
    render: (file) => noteOf(file) || "—",
  },
];

/** What happened to each file of an upload. */
const ImportResults: React.FC<{
  result: IdfaImportResult;
  /** Where the table keeps its column layout. */
  id: string;
}> = ({ result, id }) => (
  <div className={styles.results}>
    <p className={styles.meta}>{importSummary(result)}</p>
    <div className={styles.tableBox}>
      <DataTable
        id={id}
        columns={columns}
        rows={result.files}
        rowKey={(file, index) => `${file.sourcePath}:${index}`}
        defaultSort={{ key: "status", direction: "asc" }}
        emptyText="No files"
      />
    </div>
  </div>
);

export default ImportResults;
